import { FormEvent, useCallback, useContext, useState } from "react";
import Input from "@avinet/adaptive-ui-core/form/controls/Input";
import FormProvider from "@avinet/adaptive-ui-core/form/FormProvider";
import useForm from "@avinet/adaptive-ui-core/form/useForm";
import { useTranslate } from "@avinet/adaptive-ui-translate";
import { UserApi } from "../../api/UserApi";
import useProfileDetails from "../../hooks/useProfileDetails";
import useProfileDetailsExtended from "../../hooks/useProfileDetailsExtended";
import useSessionToken from "../../hooks/useSessionToken";
import { UserAccountExtended } from "../../types/UserAccount";
import { Dialog } from "./Dialog";

interface EditNameDialogProps {
  fname: string;
  lname: string;
  onClose: () => void;
}

interface UserAccountPatch {
  firstName: string;
  lastName: string;
}

export function EditNameDialog({ fname, lname, onClose }: EditNameDialogProps) {
  const t = useTranslate("EditNameDialog");
  const userData = useProfileDetailsExtended();
  const firstName = fname;
  const lastName = lname;
  const { form, state } = useForm<UserAccountPatch>({
    defaultValues: { firstName, lastName },
    langSet: "EditNameDialog",
  });
  const [isSaving, setSaving] = useState(false);
  const [error, setError] = useState<Error>();
  const sessionToken = useSessionToken();

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setError(undefined);
      setSaving(true);
      const saveObj = userData;
      if (saveObj) {
        saveObj.userData.first_name = state.firstName;
        saveObj.userData.last_name = state.lastName;
      }

      const [success, result] = await UserApi.update(
        "me",
        saveObj,
        sessionToken
      );
      if (success) onClose();
      else setError(result);
      setSaving(false);
    },
    [onClose, state.firstName, state.lastName, userData, sessionToken]
  );

  return (
    <Dialog
      title={t("title")}
      buttons={
        <>
          <button type="button" className="btn light" onClick={onClose}>
            {t("cancel")}
          </button>
          <button type="submit" className="btn primary">
            {t("submitLabel")}
          </button>
        </>
      }
      icon="person"
      onSubmit={handleSubmit}
      onClose={onClose}
      mask={isSaving}
    >
      <FormProvider form={form} state={state}>
        <p>{t("description")}</p>
        <Input type="text" autoComplete="given-name" required id="firstName" />
        <Input type="text" autoComplete="family-name" required id="lastName" />
        {error && (
          <p className="error">{t("saveError", { error: error.message })}</p>
        )}
      </FormProvider>
    </Dialog>
  );
}
